<script lang="ts">
	import Modal from './Modal.svelte';
	import AbleyButton from './AbleyButton.svelte';

	import { appStore } from '#lib/stores/appStore.svelte.js';
	import { enquiryMailto, supportMailto } from '#lib/apps/contact.js';
	import type { Environment } from '#lib/types.js';

	interface Props {
		open: boolean;
		valid: boolean;
		loading: boolean;
		oncontinue: () => void;
		onvalueinput?: (value: string) => void;
		errorMessage?: string;
		environment?: Environment;
	}

	const {
		open,
		valid,
		loading,
		oncontinue,
		onvalueinput,
		errorMessage = '',
		environment = 'production'
	}: Props = $props();

	let agreed = $state(false);

	const enquiryMail = $derived(enquiryMailto(appStore.app));
	const supportMail = $derived(supportMailto(appStore.app));

	function onInput(e: Event) {
		const trimmed = (e.target as HTMLInputElement).value.trim();
		onvalueinput?.(trimmed);
	}

	// Enter submits via implicit form submission, which also skips IME composition.
	function onSubmit(e: SubmitEvent) {
		e.preventDefault();
		if (agreed && valid) oncontinue();
	}
</script>

<Modal {open} title="Enter access key" allowDismiss={false}>
	{#snippet footer()}
		<AbleyButton type="submit" form="apiKeySetForm" disabled={!(agreed && valid)} {loading}>
			{loading ? 'Working...' : 'Confirm'}
		</AbleyButton>
	{/snippet}

	<form id="apiKeySetForm" class="keyForm" onsubmit={onSubmit}>
		<label class="field">
			<span class="label">Access key</span>
			<input
				type="text"
				placeholder="Enter key"
				autocomplete="off"
				autocapitalize="off"
				spellcheck="false"
				required
				oninput={onInput}
				id="apiKeySetInput"
				aria-invalid={errorMessage ? 'true' : undefined}
				aria-describedby={errorMessage ? 'apiKeySetError' : undefined} />
		</label>
		{#if errorMessage}
			<p class="errorMessage" id="apiKeySetError" role="alert">
				{errorMessage} If the problem persists please
				<!-- eslint-disable-next-line svelte/no-navigation-without-resolve -- mailto: link -->
				<a href={supportMail}>contact Abley</a>.
			</p>
		{/if}
		<label class="terms">
			<input type="checkbox" bind:checked={agreed} required />
			<span>
				{#if environment === 'demo'}
					This application is provided as a demonstration and is intended solely for this purpose.
					Please tick the box to acknowledge that you agree to the
					<a href="https://abley.com/en-us/terms-of-use" target="_blank">
						Terms of Use<span class="screenReaderOnly"> (opens in new tab)</span></a
					>.
				{:else if environment === 'development'}
					Abley spatial platform DEVELOPMENT environment, enter a valid key and check the box to
					enable the 'confirm' button.
				{:else if environment === 'prototype'}
					Abley spatial platform PROTOTYPE environment, enter a valid key and check the box to
					enable the 'confirm' button.
				{:else}
					<!-- FIXME: specifically references only SafeSystem, as it is our only "prod" right now -->
					Your use of this application is subject to the SafeSystem License Terms. By continuing, you
					confirm that your use complies with those terms.
				{/if}
			</span>
		</label>
		<p class="getKey">
			Don't have an access key?
			<!-- eslint-disable-next-line svelte/no-navigation-without-resolve -- mailto: link -->
			<a href={enquiryMail}>Get one now</a>
		</p>
	</form>
</Modal>

<style>
	.keyForm {
		display: flex;
		flex-direction: column;
		gap: var(--space-4);
	}

	.field {
		display: flex;
		flex-direction: column;
		gap: var(--space-1);

		.label {
			font-weight: var(--weight-semibold);
			font-size: var(--font-size-sm);
		}
	}

	.errorMessage {
		color: var(--status-danger);
		font-size: var(--font-size-sm);

		a {
			color: inherit;
		}
	}

	.terms {
		display: flex;
		align-items: flex-start;
		gap: var(--space-2);
		cursor: pointer;

		input {
			margin-top: var(--space-1);
		}
	}

	.getKey {
		color: var(--text-muted);
		font-size: var(--font-size-sm);
	}
</style>
